import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, FileText, Upload, Settings, LogOut, History, Menu, X, User, ChevronLeft, Search, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useUser } from '../../contexts/UserContext';
interface NavigationProps {
  onLogout?: () => void;
}
// Public navigation items (no auth required)
const publicNavItems = [{
  name: 'Discover',
  path: '/app',
  icon: Home,
  exact: true
}, {
  name: 'Browse',
  path: '/app/transcriptions',
  icon: FileText,
  exact: false
}, {
  name: 'Search',
  path: '/app/search',
  icon: Search,
  exact: false
}];
// Authenticated navigation items (require phone-based auth)
const authenticatedNavItems = [{
  name: 'My Dashboard',
  path: '/app/me',
  icon: Home,
  exact: true
}, {
  name: 'My History',
  path: '/app/me/history',
  icon: History,
  exact: true
}, {
  name: 'Batch Process',
  path: '/app/me/batch',
  icon: Upload,
  exact: true
}, {
  name: 'Settings',
  path: '/app/me/settings',
  icon: Settings,
  exact: true
}];
export function Navigation({
  onLogout
}: NavigationProps) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Load collapsed state from localStorage
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  const {
    user,
    supabaseUser,
    session
  } = useUser();
  // Determine which nav items to show based on auth status
  const navItems = session ? authenticatedNavItems : publicNavItems;
  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', isCollapsed.toString());
  }, [isCollapsed]);
  // Close mobile menu when location changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };
  const toggleCollapsed = () => {
    setIsCollapsed(!isCollapsed);
  };
  return <>
      {/* Desktop Navigation */}
      <nav className={cn('h-[calc(100vh-2rem)] sticky top-4 mx-4 p-5 hidden md:flex md:flex-col bg-card/95 rounded-2xl shadow-lg ios-blur overflow-hidden transition-all duration-300 hover:shadow-xl', isCollapsed ? 'w-20' : 'w-60')} data-id="element-711">
        {/* Toggle Button */}
        <button onClick={toggleCollapsed} className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-secondary text-foreground/50 hover:text-foreground transition-colors z-10" aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} data-id="element-712">
          {isCollapsed ? <ChevronsRight size={18} data-id="element-713" /> : <ChevronsLeft size={18} data-id="element-714" />}
        </button>

        <div className="flex-shrink-0 mb-8 px-2" data-id="element-715">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6tsscMEhsZ3L4JwT2r374e/ScribeTok-logo.png" alt="ScribeTok Logo" className={cn('h-10 w-auto transition-opacity duration-200', isCollapsed && 'opacity-0')} data-id="element-716" />
        </div>

        {user && <div className={cn('flex-shrink-0 mb-6 p-3 bg-primary/5 rounded-xl transition-all duration-200', isCollapsed ? 'flex flex-col items-center' : 'flex items-center')} data-id="element-717">
            <div className={cn('rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0', isCollapsed ? 'w-10 h-10' : 'w-8 h-8 mr-3')} data-id="element-718">
              <User size={isCollapsed ? 18 : 14} className="text-primary" data-id="element-719" />
            </div>
            {!isCollapsed && <div className="overflow-hidden" data-id="element-720">
                <p className="text-sm font-medium truncate" data-id="element-721">{user.email}</p>
                <div className="flex items-center" data-id="element-722">
                  <p className="text-xs text-muted-foreground" data-id="element-723">
                    {user.plan.isPro ? 'Pro Plan' : 'Free Plan'}
                  </p>
                  {!user.plan.isPro && <span className="ml-2 text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded-full" data-id="element-724">
                      {Math.max(0, 3 - user.usage.minutesUsed)} credits left
                    </span>}
                </div>
              </div>}
          </div>}

        <div className="flex-grow overflow-y-auto min-h-0" data-id="element-725">
          <div className="space-y-1" data-id="element-726">
            {navItems.map(item => {
            const isActive = item.exact ? location.pathname === item.path : location.pathname === item.path || item.path !== '/app' && location.pathname.startsWith(item.path);
            const Icon = item.icon;
            return <Link key={item.path} to={item.path} className={cn('flex items-center h-11 px-3 py-2 rounded-xl text-sm font-medium', 'transition-colors duration-200 group relative', isActive ? 'text-primary-foreground tiktok-gradient' : 'text-foreground/70 hover:text-foreground hover:bg-secondary', isCollapsed && 'justify-center')} title={isCollapsed ? item.name : undefined} data-id="element-727">
                  <Icon size={18} className={cn(isCollapsed ? '' : 'mr-3', isActive ? 'text-primary-foreground' : 'text-foreground/50 group-hover:text-foreground/70')} data-id="element-728" />
                  {!isCollapsed && <span data-id="element-729">{item.name}</span>}
                </Link>;
          })}
          </div>
        </div>

        {onLogout && session && <div className="flex-shrink-0 mt-auto pt-4 border-t border-border/40" data-id="element-730">
            <button onClick={onLogout} className={cn('flex w-full items-center h-11 px-3 py-2 rounded-xl text-sm font-medium text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors duration-200', isCollapsed && 'justify-center')} title={isCollapsed ? 'Sign Out' : undefined} data-id="element-731">
              <LogOut size={18} className={cn(isCollapsed ? '' : 'mr-3', 'text-foreground/50')} data-id="element-732" />
              {!isCollapsed && <span data-id="element-733">Sign Out</span>}
            </button>
          </div>}
      </nav>

      {/* Mobile Navigation Header */}
      <div className="md:hidden sticky top-0 z-30 bg-card/80 ios-blur ios-shadow safe-top" data-id="element-734">
        <div className="flex items-center justify-between p-4 border-b border-border/20" data-id="element-735">
          <div className="flex items-center" data-id="element-736">
            {location.pathname !== '/app' && <Link to="/app" className="mr-3" data-id="element-737">
                <ChevronLeft size={24} className="text-foreground/70" data-id="element-738" />
              </Link>}
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6tsscMEhsZ3L4JwT2r374e/ScribeTok-logo.png" alt="ScribeTok Logo" className="h-8 w-auto" data-id="element-739" />
          </div>
          <button onClick={toggleMobileMenu} className="p-2 rounded-full hover:bg-secondary text-foreground/70 hover:text-foreground transition-colors active:scale-95" aria-label="Toggle menu" data-id="element-740">
            {mobileMenuOpen ? <X size={24} data-id="element-741" /> : <Menu size={24} data-id="element-742" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm transition-opacity animate-fade-in" onClick={closeMobileMenu} data-id="element-743">
          <div className="fixed inset-y-0 left-0 w-[85%] max-w-xs bg-card/95 ios-blur ios-shadow-lg transform transition-transform duration-300 ease-in-out animate-slide-right overflow-y-auto safe-top safe-bottom" onClick={e => e.stopPropagation()} data-id="element-744">
            <div className="flex justify-between items-center p-5 border-b border-border/20" data-id="element-745">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6tsscMEhsZ3L4JwT2r374e/ScribeTok-logo.png" alt="ScribeTok Logo" className="h-8 w-auto" data-id="element-746" />
              <button onClick={closeMobileMenu} className="p-2 rounded-full hover:bg-secondary text-foreground/70 hover:text-foreground transition-colors" aria-label="Close menu" data-id="element-747">
                <X size={20} data-id="element-748" />
              </button>
            </div>
            {user && <div className="mx-4 my-5 p-4 bg-primary/5 rounded-xl flex items-center" data-id="element-749">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mr-3" data-id="element-750">
                  <User size={16} className="text-primary" data-id="element-751" />
                </div>
                <div className="overflow-hidden" data-id="element-752">
                  <p className="font-medium truncate" data-id="element-753">{user.email}</p>
                  <p className="text-sm text-muted-foreground" data-id="element-754">
                    {user.plan.isPro ? 'Pro Plan' : 'Free Plan'}
                  </p>
                </div>
              </div>}
            <div className="p-4 space-y-2" data-id="element-755">
              {navItems.map(item => {
            const isActive = item.exact ? location.pathname === item.path : location.pathname === item.path || item.path !== '/app' && location.pathname.startsWith(item.path);
            const Icon = item.icon;
            return <Link key={item.path} to={item.path} className={cn('flex items-center h-12 px-4 py-3 rounded-xl text-base font-medium', 'transition-colors duration-200 group relative', isActive ? 'text-primary-foreground tiktok-gradient' : 'text-foreground/70 hover:text-foreground hover:bg-secondary')} onClick={closeMobileMenu} data-id="element-756">
                    <Icon size={20} className={cn('mr-3', isActive ? 'text-primary-foreground' : 'text-foreground/50 group-hover:text-foreground/70')} data-id="element-757" />
                    <span data-id="element-758">{item.name}</span>
                  </Link>;
          })}
            </div>
            {onLogout && session && <div className="absolute bottom-8 left-4 right-4 pt-4 border-t border-border/20" data-id="element-759">
                <button onClick={() => {
            closeMobileMenu();
            onLogout();
          }} className="flex w-full items-center h-12 px-4 py-3 rounded-xl text-base font-medium text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors duration-200" data-id="element-760">
                  <LogOut size={20} className="mr-3 text-foreground/50" data-id="element-761" />
                  <span data-id="element-762">Sign Out</span>
                </button>
              </div>}
          </div>
        </div>}
    </>;
}